<template>
  <el-dialog
    :title="$t('参数调整')"
    :visible.sync="visible"
    width="80%"
    top="5vh"
    append-to-body
    :before-close="closeDialog"
    :close-on-click-modal="false"
    destroy-on-close
  >
    <v-main :loading="loading" :scene-list="sceneList"></v-main>
    <span slot="footer" class="dialog-footer">
      <el-button @click="closeDialog">{{ $t('取消') }}</el-button>
      <el-button type="primary" :loading="resetLoading" @click="valueReset">
        {{ $t('恢复默认值') }}
      </el-button>
      <el-button type="primary" @click="valueSave" :loading="saveLoading">
        {{ $t('确定') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import VMain from './main.vue';
import { sceneMap, getSceneList } from './utils/dict';
import { queryParams, updateParams } from '@/api/params';
import { cloneDeep, mergeWith } from 'lodash';
import { makeMap } from '@/utils/utils';

export default {
  components: {
    VMain
  },
  props: {
    sn: String,
    deviceType: String,
    visible: Boolean
  },
  data() {
    const sceneList = getSceneList(this.deviceType);
    return {
      i18nPath: 'component.deviceParamDialog',
      saveLoading: false,
      resetLoading: false,
      params: [
        { paramSceneCode: sceneList[0][1], paramSceneName: sceneList[0][0], data: [] },
        { paramSceneCode: sceneList[1][1], paramSceneName: sceneList[1][0], data: [] }
      ],
      model: {
        sceneCurrent: 1,
        preventFlag: 0
      },
      oldModel: '',
      loading: true
    };
  },
  provide() {
    return {
      params: this.params,
      model: this.model,
      sn: this.sn,
      deviceType: this.deviceType
    };
  },
  computed: {
    sceneList() {
      const sceneList = getSceneList(this.deviceType);

      return sceneList.map(e => [this.$t(e[0]), e[1]]);
    }
  },
  watch: {
    visible: {
      handler(n) {
        if (!n) return;
        this.getParam(1);
      },
      immediate: true
    }
  },
  methods: {
    /** 获取设备参数配置 */
    getParam(p) {
      this.loading = this.resetLoading = true;
      queryParams(this.sn, p)
        .then(res => {
          if (p === 2) return this.resetParams(res);
          this.initData(res, this.params);
          this.initModel({ ...res.data });
        })
        .finally(() => {
          this.loading = this.resetLoading = false;
        });
    },
    /** listJSON 转换 &quot; 转双引号 */
    listConverter(str) {
      if (!str) return [];
      const reg = [/\&quot;/g, '"'];
      str = str.replace(...reg);
      return JSON.parse(str).map(e => [e[0], `${e[1]}`]);
    },
    /** 初始化数据结构 */
    initData(res, params) {
      const items = res.data.paramItems;
      let lowVal = null;
      let highVal = null;
      delete res.data.paramItems;
      items.forEach(item => {
        item.displayType = item.displayType || 'input';
        item.itemName = item.itemName || item.itemCode;
        item.itemList = this.listConverter(item.itemList);
        // 如果是select元素并且多选
        if (item.displayType === 'multiple') {
          lowVal = item.value['low'] ? item.value['low'].split(',') : [];
          highVal = item.value['high'] ? item.value['high'].split(',') : [];
        } else {
          lowVal = item.value['low'];
          highVal = item.value['high'];
        }
        params[0].data.push({ ...item, value: lowVal });
        params[1].data.push({ ...item, value: highVal });
      });
    },
    initModel(data) {
      Object.assign(this.model, data);
      this.extractParamModel();
      this.recordOriginalModel();
    },
    extractParamModel() {
      // 属性去重
      let m = {};
      // 对应的场景key high low middle
      let key = '';
      // 用于装载一个参数的全部属性
      let target = null;
      let val = null;
      // 遍历每个场景中的每条参数 进行处理
      this.params.forEach(item => {
        key = sceneMap[item.paramSceneCode];
        item.data.forEach(e => {
          target = m[e.itemCode] = m[e.itemCode] || {};
          val = e.displayType === 'multiple' ? e.value.join(',') : e.value;
          (target.value = target.value || {})[key] = val;
          target.id = e.id;
          target.batchId = e.batchId;
        });
      });
      this.model.paramItems = Object.keys(m).map(key => {
        return { itemCode: key, ...m[key] };
      });
    },
    recordOriginalModel() {
      this.oldModel = JSON.stringify(this.model);
    },
    closeDialog() {
      this.extractParamModel();
      if (this.oldModel === JSON.stringify(this.model)) {
        this.$emit('update:visible', false);
        this.resetOption();
      } else {
        this.$confirm(this.$t('是否放弃本次修改?'), this.$t('提示'), {
          confirmButtonText: this.$t('确定'),
          cancelButtonText: this.$t('取消'),
          type: 'warning'
        })
          .then(() => {
            this.resetOption();
            this.$emit('update:visible', false);
          })
          .catch(() => {});
      }
    },
    resetParams(res) {
      const newParams = cloneDeep(this.params);
      this.resetOption('params', newParams);
      this.initData(res, newParams);
      mergeWith(this.params, newParams, (objValue, srcValue) => {
        if (Array.isArray(objValue) && Array.isArray(srcValue)) {
          return srcValue.splice(0, Infinity, objValue);
        }
      });
    },
    resetOption(opt = 'loading,resetLoading,params', params) {
      params = params || this.params;
      const valid = makeMap(opt);
      valid('loading') && (this.loading = true);
      valid('resetLoading') && (this.resetLoading = false);
      valid('params') && params.forEach(e => e.data.splice(0, Infinity));
    },
    valueSave() {
      this.saveLoading = true;
      this.extractParamModel();
      updateParams(this.model)
        .then(() => {
          this.$message({
            type: 'success',
            message: this.$t('设置成功')
          });
          this.$emit('update:visible', false);
          this.resetOption();
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },
    valueReset() {
      this.getParam(2);
    }
  }
};
</script>

<style></style>
